import React, { useState } from 'react';
import { Button, Alert } from 'reactstrap';
import styles from './update-subscription-popup.module.scss';
import { PLAN_NAME } from 'src/pages/constants';
import { Coupon, Plan } from 'src/pages/deprecated/payment/types';
import {
  getCurrencySymbol,
  isUserFromNewZealand,
  formatIntervalDisplay,
  isChallengeBundleActive,
  detectActiveCurrency,
} from 'src/lib/utils';
import { useSelector } from 'react-redux';
import { IAppState } from 'src/store';
import AllSubscriptions from 'src/models/AllSubscriptions';

type UpdateSubscriptionSummaryProps = {
  plan: Plan;
  weeklyMealPlanQty: number;
  currentCheckinCount: number | undefined;
  setPopupVisible: any;
  purchaseSubscription: () => Promise<void>;
  coupon?: Coupon;
  allSubscriptions: AllSubscriptions | undefined;
};

export const UpdateSubscriptionSummary = ({
  plan,
  weeklyMealPlanQty,
  currentCheckinCount,
  setPopupVisible,
  purchaseSubscription,
  coupon,
  allSubscriptions,
}: UpdateSubscriptionSummaryProps) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const user = useSelector((state: IAppState) => state.auth?.user);

  const activeCurrency = detectActiveCurrency(allSubscriptions);

  const isNewZealandUser = activeCurrency
    ? activeCurrency === 'nzd'
    : !isChallengeBundleActive() &&
      (isUserFromNewZealand() || (user?.country ?? '') === 'New Zealand');

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      await purchaseSubscription();
      setIsSubmitting(false);
      setPopupVisible(false);
    } catch (error) {
      setErrorMessage(
        'There was an error updating your subscription, please try again or contact us'
      );
    }
  };

  const price = Number(plan.mealPlanVariants[weeklyMealPlanQty]?.unitAmount);

  let amountOff = 0;
  if (coupon) {
    amountOff =
      (plan.mealPlanVariants[weeklyMealPlanQty]?.unitAmountCents *
        coupon.percentOff) /
      10000;
  }

  return (
    <div>
      <h3>Summary</h3>
      <div className={styles.field}>
        <div className={styles.inner}>
          <div className={styles.text}>{`${plan.id} subscription`}</div>
          <div className={styles.price}>
            {getCurrencySymbol(activeCurrency || isNewZealandUser) +
              Number(
                plan.mealPlanVariants[weeklyMealPlanQty]?.unitAmount
              ).toFixed(2)}
          </div>
        </div>
      </div>
      {!!coupon && !!amountOff && (
        <div className={styles.field}>
          <div className={styles.inner}>
            <div className={styles.text}>{coupon.percentOff}% off</div>
            <div className={styles.price}>
              {getCurrencySymbol(activeCurrency || isNewZealandUser)} -
              {amountOff.toFixed(2)}
            </div>
          </div>
        </div>
      )}
      <hr />
      <div className={styles.field}>
        <div className={styles.inner}>
          <div className={styles.text}>Total</div>
          <div className={styles.price}>
            {getCurrencySymbol(activeCurrency || isNewZealandUser) +
              (price - Number(amountOff.toFixed(2))).toFixed(2)}{' '}
            every{' '}
            {formatIntervalDisplay(plan?.intervalCount || 1, plan?.interval)}
          </div>
        </div>
      </div>
      {!PLAN_NAME.STANDARD_PLAN.includes(plan.id) &&
      currentCheckinCount &&
      currentCheckinCount < 8 ? (
        <div className={styles.field}>
          <div className={styles.inner}>
            <p>
              Please note, changes will take effect after your minimum 8 week
              subscription
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.field}>
            <div className={styles.inner}>
              <p>
                Please note, changes will take effect on your next billing
                cycle.
              </p>
            </div>
          </div>

          {errorMessage !== '' && <Alert color="danger">{errorMessage}</Alert>}
        </>
      )}
      <Button block onClick={() => handleSubmit()} disabled={isSubmitting}>
        Update subscription
      </Button>
    </div>
  );
};
